import * as Sentry from '@sentry/browser';

import BulkExerciseCreateSession from '../../../../Model/BulkExerciseCreateSession';

/**
 * Firestore-backed session persistence so an accidentally closed browser
 * doesn't lose the batch. Only serializable card metadata is stored — the
 * File objects themselves cannot survive a reload, so interrupted uploads are
 * restored in a "re-select the file" state (see utils.restoreCard).
 *
 * Session docs have auto-generated ids and belong to a (coachId, createdBy)
 * pair, so an AC's interrupted batch never surfaces for the coach or vice
 * versa. All operations are best-effort: a persistence failure is logged but
 * never breaks the creator view itself.
 */
const loadSession = async (coachId, authUserId) => {
  try {
    const sessionDoc = await BulkExerciseCreateSession.getByCoachAndCreator(coachId, authUserId);
    if (!sessionDoc) {
      return null;
    }
    return { id: sessionDoc.id, autoSave: sessionDoc.autoSave, cards: sessionDoc.cards };
  } catch (error) {
    Sentry.captureException(error, {
      extra: { message: 'Failed to load bulk-create session', coachId },
    });
    return null;
  }
};

// Updates the known session doc, or creates one (returning its new id) on the
// first write. Callers should hold on to the returned id for later writes.
const saveSession = async ({
  sessionId, coachId, createdBy, autoSave, cards,
}) => {
  try {
    if (sessionId) {
      await BulkExerciseCreateSession.update(sessionId, { autoSave, cards });
      return sessionId;
    }
    return await BulkExerciseCreateSession.create({
      coachId, createdBy, autoSave, cards,
    });
  } catch (error) {
    Sentry.captureException(error, {
      extra: { message: 'Failed to persist bulk-create session', coachId },
    });
    return sessionId || null;
  }
};

// Marks the session finished instead of deleting it — completed docs are kept
// as a history of bulk-creation batches and no longer surface for recovery.
const completeSession = (sessionId) => {
  if (!sessionId) {
    return;
  }
  BulkExerciseCreateSession.completeById(sessionId).catch((error) => {
    Sentry.captureException(error, {
      extra: { message: 'Failed to complete bulk-create session', sessionId },
    });
  });
};

export {
  loadSession,
  saveSession,
  completeSession,
};
